import {
  FINANCE_PNL_MANUAL_ADMIN_SOURCE_SYSTEM_REF,
  FINANCE_PNL_PUBLIC_FILING_SOURCE_SYSTEM_REF,
} from './approved-source-systems';
import { valueDigestInput } from './approved-formula-value-seal';
import { isNonEmptyString, isRecord } from './ask-helix/contracts/program-receipt-shape-predicates';
import type {
  ApprovedFormulaBlockedResult,
  ApprovedFormulaDefinition,
  ApprovedFormulaEvidenceArtifact,
  ApprovedFormulaEvidenceValidationBlock,
  ApprovedFormulaGoldenTrace,
  ApprovedFormulaImplementationDigestInput,
  ApprovedFormulaReconciliationRule,
  ApprovedFormulaResult,
} from './approved-formulas';

export const CURRENT_FY_ESTIMATE_FACT_SCHEMA_VERSION = 'current_fy_estimate_fact_v1' as const;
export const CURRENT_FY_ESTIMATE_FORMULA_ID = 'current_fy_estimate_usd_v1' as const;
export const CURRENT_FY_ESTIMATE_FORMULA_VERSION = '1.0.0' as const;
export const CURRENT_FY_ESTIMATE_FORMULA_RUNTIME_VERSION = 'approved_formula_runtime_v1' as const;
export const CURRENT_FY_ESTIMATE_FORMULA_CANONICALIZER_VERSION = 'approved_formula_canonicalizer_v1' as const;
export const CURRENT_FY_ESTIMATE_FORMULA_INPUT_SCHEMA_VERSION = 'current_fy_estimate_input_v1' as const;
export const CURRENT_FY_ESTIMATE_FORMULA_OUTPUT_SCHEMA_VERSION = 'current_fy_estimate_output_v1' as const;
export const CURRENT_FY_ESTIMATE_CONTEXT_KIND = 'decision_agent:finance:current_fy_estimate_fact' as const;

export type CurrentFyEstimateFactComponentKind = 'actual_to_date' | 'forecast_remainder';
export type CurrentFyEstimateMetricValueKind = 'additive_usd';
export type CurrentFyEstimateCurrency = 'USD';
export type CurrentFyEstimateScope = Readonly<{ scopeKind: string; scopeId: string }>;

export type CurrentFyEstimateFact = Readonly<{
  schemaVersion: typeof CURRENT_FY_ESTIMATE_FACT_SCHEMA_VERSION;
  factId: string;
  componentKind: CurrentFyEstimateFactComponentKind;
  metricKey: string;
  metricValueKind: CurrentFyEstimateMetricValueKind;
  fiscalYear: number;
  scope: CurrentFyEstimateScope;
  currency: CurrentFyEstimateCurrency;
  periodStartMonth: number;
  periodEndMonth: number;
  valueUsd: number;
  sourceRef: string;
  reportingCutAsOf?: string;
}>;

export type CurrentFyEstimateInput = Readonly<{ facts: readonly CurrentFyEstimateFact[] }>;

export type CurrentFyEstimateOutput = Readonly<{
  primaryMetricKey: string;
  primaryMetricValue: number;
  metricValueKind: CurrentFyEstimateMetricValueKind;
  fiscalYear: number;
  scope: CurrentFyEstimateScope;
  currency: CurrentFyEstimateCurrency;
  actualToDateUsd: number;
  forecastRemainderUsd: number;
  currentFyEstimateUsd: number;
  actualPeriodStartMonth: number;
  actualPeriodEndMonth: number;
  forecastPeriodStartMonth: number;
  forecastPeriodEndMonth: number;
}>;

const COMPLETE_FY_START_MONTH = 1;
const COMPLETE_FY_END_MONTH = 12;
export const CURRENT_FY_ESTIMATE_APPROVED_SOURCE_SYSTEM_REFS = Object.freeze([
  FINANCE_PNL_PUBLIC_FILING_SOURCE_SYSTEM_REF, FINANCE_PNL_MANUAL_ADMIN_SOURCE_SYSTEM_REF,
] as const);

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isFiscalMonth(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) &&
    value >= COMPLETE_FY_START_MONTH && value <= COMPLETE_FY_END_MONTH;
}

function blocked(
  reasonCode: ApprovedFormulaBlockedResult['reasonCode'],
  message: string,
): ApprovedFormulaBlockedResult {
  return Object.freeze({ status: 'blocked' as const, reasonCode, message });
}

function currentFyEstimateScopeIssue(value: unknown, label: string): string | null {
  if (!isRecord(value)) return `${label}.scope must be an object`;
  if (!isNonEmptyString(value.scopeKind)) return `${label}.scope.scopeKind is required`;
  if (!isNonEmptyString(value.scopeId)) return `${label}.scope.scopeId is required`;
  return null;
}

function currentFyEstimateFactIssue(value: unknown, label: string): string | null {
  if (!isRecord(value)) return `${label} must be an object`;
  if (value.schemaVersion !== CURRENT_FY_ESTIMATE_FACT_SCHEMA_VERSION) {
    return `${label}.schemaVersion must be ${CURRENT_FY_ESTIMATE_FACT_SCHEMA_VERSION}`;
  }
  if (!isNonEmptyString(value.factId)) return `${label}.factId is required`;
  if (value.componentKind !== 'actual_to_date' && value.componentKind !== 'forecast_remainder') {
    return `${label}.componentKind must be actual_to_date or forecast_remainder`;
  }
  if (!isNonEmptyString(value.metricKey)) return `${label}.metricKey is required`;
  if (value.metricValueKind !== 'additive_usd') {
    return `${label}.metricValueKind must be additive_usd`;
  }
  if (!Number.isInteger(value.fiscalYear)) return `${label}.fiscalYear must be an integer`;
  const scopeIssue = currentFyEstimateScopeIssue(value.scope, label);
  if (scopeIssue) return scopeIssue;
  if (value.currency !== 'USD') return `${label}.currency must be USD`;
  if (!isFiscalMonth(value.periodStartMonth)) return `${label}.periodStartMonth is invalid`;
  if (!isFiscalMonth(value.periodEndMonth)) return `${label}.periodEndMonth is invalid`;
  if (value.periodStartMonth > value.periodEndMonth) {
    return `${label}.periodStartMonth must be <= periodEndMonth`;
  }
  if (!isFiniteNumber(value.valueUsd)) return `${label}.valueUsd must be finite`;
  if (!isNonEmptyString(value.sourceRef)) return `${label}.sourceRef is required`;
  if (value.reportingCutAsOf !== undefined && !isNonEmptyString(value.reportingCutAsOf)) {
    return `${label}.reportingCutAsOf must be non-empty when present`;
  }
  return null;
}

export function validateCurrentFyEstimateFact(
  value: unknown,
):
  | Readonly<{ status: 'ok'; fact: CurrentFyEstimateFact }>
  | Readonly<{ status: 'invalid'; message: string }> {
  const message = currentFyEstimateFactIssue(value, 'fact');
  if (message) return Object.freeze({ status: 'invalid' as const, message });
  return Object.freeze({ status: 'ok' as const, fact: value as CurrentFyEstimateFact });
}

export function isCurrentFyEstimateFact(value: unknown): value is CurrentFyEstimateFact {
  return validateCurrentFyEstimateFact(value).status === 'ok';
}

function currentFyEstimateInputIssue(
  input: CurrentFyEstimateInput,
): ApprovedFormulaBlockedResult | null {
  if (!isRecord(input) || !Array.isArray(input.facts)) {
    return blocked('missing_formula_input', 'current-FY estimate facts are required');
  }
  if (input.facts.length !== 2) {
    return blocked(
      'invalid_formula_input',
      'current-FY estimate requires exactly two facts: one actual_to_date and one forecast_remainder',
    );
  }
  for (const [index, candidate] of input.facts.entries()) {
    const issue = currentFyEstimateFactIssue(candidate, `fact ${index + 1}`);
    if (issue) return blocked('invalid_formula_input', issue);
  }
  const factIds = new Set(input.facts.map((fact) => fact.factId));
  if (factIds.size !== input.facts.length) {
    return blocked('invalid_formula_input', 'current-FY estimate factIds must be unique');
  }
  return null;
}

type CurrentFyEstimatePair = Readonly<{ actual: CurrentFyEstimateFact; forecast: CurrentFyEstimateFact }>;

type CurrentFyEstimatePairResult =
  | Readonly<{ status: 'ok'; pair: CurrentFyEstimatePair }>
  | ApprovedFormulaBlockedResult;

function sameCurrentFyEstimateScope(
  left: CurrentFyEstimateScope,
  right: CurrentFyEstimateScope,
): boolean {
  return left.scopeKind === right.scopeKind && left.scopeId === right.scopeId;
}

function currentFyEstimateIdentityIssue(
  actual: CurrentFyEstimateFact,
  forecast: CurrentFyEstimateFact,
): string | null {
  if (actual.metricValueKind !== 'additive_usd' || forecast.metricValueKind !== 'additive_usd') {
    return 'current-FY estimate only admits additive_usd metric values';
  }
  if (actual.currency !== 'USD' || forecast.currency !== 'USD') {
    return 'current-FY estimate only admits USD currency';
  }
  if (actual.metricKey !== forecast.metricKey) {
    return 'actual_to_date and forecast_remainder must use the same metricKey';
  }
  if (actual.fiscalYear !== forecast.fiscalYear) {
    return 'actual_to_date and forecast_remainder must use the same fiscalYear';
  }
  if (!sameCurrentFyEstimateScope(actual.scope, forecast.scope)) {
    return 'actual_to_date and forecast_remainder must use the same scope';
  }
  return null;
}

function currentFyEstimatePeriodIssue(
  actual: CurrentFyEstimateFact,
  forecast: CurrentFyEstimateFact,
): string | null {
  if (actual.periodStartMonth !== COMPLETE_FY_START_MONTH) {
    return 'actual_to_date period must start at fiscal month 1';
  }
  if (forecast.periodEndMonth !== COMPLETE_FY_END_MONTH) {
    return 'forecast_remainder period must end at fiscal month 12';
  }
  if (actual.periodEndMonth >= forecast.periodStartMonth) {
    return 'actual_to_date and forecast_remainder periods must not overlap';
  }
  if (actual.periodEndMonth + 1 !== forecast.periodStartMonth) {
    return 'actual_to_date and forecast_remainder must cover fiscal months 1 through 12 without gaps';
  }
  return null;
}

function currentFyEstimatePair(input: CurrentFyEstimateInput): CurrentFyEstimatePairResult {
  const inputIssue = currentFyEstimateInputIssue(input);
  if (inputIssue) return inputIssue;
  const actual = input.facts.find((fact) => fact.componentKind === 'actual_to_date');
  const forecast = input.facts.find((fact) => fact.componentKind === 'forecast_remainder');
  if (!actual || !forecast) {
    return blocked(
      'invalid_formula_input',
      'current-FY estimate requires exactly one actual_to_date fact and one forecast_remainder fact',
    );
  }
  const identityIssue = currentFyEstimateIdentityIssue(actual, forecast);
  if (identityIssue) return blocked('invalid_formula_input', identityIssue);
  const periodIssue = currentFyEstimatePeriodIssue(actual, forecast);
  if (periodIssue) return blocked('invalid_formula_input', periodIssue);
  return Object.freeze({ status: 'ok' as const, pair: Object.freeze({ actual, forecast }) });
}

function roundUsd(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function executeCurrentFyEstimate(
  input: CurrentFyEstimateInput,
): ApprovedFormulaResult<CurrentFyEstimateOutput> {
  const pairResult = currentFyEstimatePair(input);
  if (pairResult.status === 'blocked') return pairResult;

  const actualToDateUsd = roundUsd(pairResult.pair.actual.valueUsd);
  const forecastRemainderUsd = roundUsd(pairResult.pair.forecast.valueUsd);
  const currentFyEstimateUsd = roundUsd(actualToDateUsd + forecastRemainderUsd);
  return Object.freeze({
    status: 'ok' as const,
    output: Object.freeze({
      primaryMetricKey: pairResult.pair.actual.metricKey,
      primaryMetricValue: currentFyEstimateUsd,
      metricValueKind: 'additive_usd' as const,
      fiscalYear: pairResult.pair.actual.fiscalYear,
      scope: Object.freeze({ ...pairResult.pair.actual.scope }),
      currency: 'USD' as const,
      actualToDateUsd,
      forecastRemainderUsd,
      currentFyEstimateUsd,
      actualPeriodStartMonth: pairResult.pair.actual.periodStartMonth,
      actualPeriodEndMonth: pairResult.pair.actual.periodEndMonth,
      forecastPeriodStartMonth: pairResult.pair.forecast.periodStartMonth,
      forecastPeriodEndMonth: pairResult.pair.forecast.periodEndMonth,
    }),
  });
}

function evidenceBlockFromFormulaBlock(
  block: ApprovedFormulaBlockedResult,
): ApprovedFormulaEvidenceValidationBlock {
  const reasonCode =
    block.reasonCode === 'unapproved_source_system'
      ? 'unapproved_source_system'
      : 'invalid_formula_input';
  return Object.freeze({ reasonCode, message: block.message });
}

function evidenceReconciliationBlock(message: string): ApprovedFormulaEvidenceValidationBlock {
  return Object.freeze({ reasonCode: 'formula_reconciliation_failure' as const, message });
}

function sourceSystemRefApproved(value: unknown): boolean {
  return typeof value === 'string' && CURRENT_FY_ESTIMATE_APPROVED_SOURCE_SYSTEM_REFS.includes(
    value as (typeof CURRENT_FY_ESTIMATE_APPROVED_SOURCE_SYSTEM_REFS)[number],
  );
}

function evidenceForFact(
  fact: CurrentFyEstimateFact,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceArtifact | null {
  return artifacts.find((artifact) => artifact.sourceRef === fact.sourceRef) ?? null;
}

function sameOptionalString(left: string | undefined, right: string | undefined): boolean {
  return (left ?? null) === (right ?? null);
}

function sameCurrentFyEstimateFact(
  left: CurrentFyEstimateFact,
  right: CurrentFyEstimateFact,
): boolean {
  return (
    left.schemaVersion === right.schemaVersion &&
    left.factId === right.factId &&
    left.componentKind === right.componentKind &&
    left.metricKey === right.metricKey &&
    left.metricValueKind === right.metricValueKind &&
    left.fiscalYear === right.fiscalYear &&
    sameCurrentFyEstimateScope(left.scope, right.scope) &&
    left.currency === right.currency &&
    left.periodStartMonth === right.periodStartMonth &&
    left.periodEndMonth === right.periodEndMonth &&
    left.valueUsd === right.valueUsd &&
    left.sourceRef === right.sourceRef &&
    sameOptionalString(left.reportingCutAsOf, right.reportingCutAsOf)
  );
}

function currentFyEstimateArtifactContentBlock(
  fact: CurrentFyEstimateFact,
  artifact: ApprovedFormulaEvidenceArtifact,
): ApprovedFormulaEvidenceValidationBlock | null {
  if (artifact.schemaVersion !== CURRENT_FY_ESTIMATE_FACT_SCHEMA_VERSION) {
    return evidenceReconciliationBlock(
      `current-FY estimate evidence for ${fact.factId} has the wrong schemaVersion`,
    );
  }
  const validation = validateCurrentFyEstimateFact(artifact.contentJson);
  if (validation.status === 'invalid' || !sameCurrentFyEstimateFact(fact, validation.fact)) {
    return evidenceReconciliationBlock(
      `current-FY estimate fact ${fact.factId} must reconcile to typed governed evidence content`,
    );
  }
  if (!sameOptionalString(fact.reportingCutAsOf, artifact.reportingCutAsOf)) {
    return evidenceReconciliationBlock(
      `current-FY estimate fact ${fact.factId} reporting cut must match governed evidence`,
    );
  }
  return null;
}

export function validateCurrentFyEstimateInputAgainstEvidence(
  input: CurrentFyEstimateInput,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const pairResult = currentFyEstimatePair(input);
  if (pairResult.status === 'blocked') return evidenceBlockFromFormulaBlock(pairResult);

  for (const fact of input.facts) {
    const artifact = evidenceForFact(fact, artifacts);
    if (!artifact) {
      return Object.freeze({
        reasonCode: 'missing_governed_evidence' as const,
        message: `current-FY estimate fact ${fact.factId} must reference governed evidence`,
      });
    }
    if (!sourceSystemRefApproved(artifact.sourceSystemRef)) {
      return Object.freeze({
        reasonCode: 'unapproved_source_system' as const,
        message: `current-FY estimate fact ${fact.factId} references an unapproved source system`,
      });
    }
    const contentBlock = currentFyEstimateArtifactContentBlock(fact, artifact);
    if (contentBlock) return contentBlock;
  }
  return null;
}

function sameCurrentFyEstimateOutput(
  left: Readonly<CurrentFyEstimateOutput>,
  right: Readonly<CurrentFyEstimateOutput>,
): boolean {
  return (
    left.primaryMetricKey === right.primaryMetricKey &&
    left.primaryMetricValue === right.primaryMetricValue &&
    left.metricValueKind === right.metricValueKind &&
    left.fiscalYear === right.fiscalYear &&
    sameCurrentFyEstimateScope(left.scope, right.scope) &&
    left.currency === right.currency &&
    left.actualToDateUsd === right.actualToDateUsd &&
    left.forecastRemainderUsd === right.forecastRemainderUsd &&
    left.currentFyEstimateUsd === right.currentFyEstimateUsd &&
    left.actualPeriodStartMonth === right.actualPeriodStartMonth &&
    left.actualPeriodEndMonth === right.actualPeriodEndMonth &&
    left.forecastPeriodStartMonth === right.forecastPeriodStartMonth &&
    left.forecastPeriodEndMonth === right.forecastPeriodEndMonth
  );
}

export function reconcileCurrentFyEstimateOutput(
  input: CurrentFyEstimateInput,
  output: Readonly<CurrentFyEstimateOutput>,
): ApprovedFormulaEvidenceValidationBlock | null {
  const executed = executeCurrentFyEstimate(input);
  if (executed.status === 'blocked') return evidenceBlockFromFormulaBlock(executed);
  if (sameCurrentFyEstimateOutput(executed.output, output)) return null;
  return Object.freeze({
    reasonCode: 'formula_reconciliation_failure' as const,
    message: 'current-FY estimate output must reconcile to the approved formula execution',
  });
}

const CURRENT_FY_ESTIMATE_FORMULA_METADATA = Object.freeze({
  formulaId: CURRENT_FY_ESTIMATE_FORMULA_ID,
  version: CURRENT_FY_ESTIMATE_FORMULA_VERSION,
  displayName: 'Current fiscal-year estimate',
  description:
    'Adds actual-to-date and non-overlapping forecast-remainder facts for one additive USD metric.',
  lifecycle: 'code_defined' as const,
  formulaRuntimeVersion: CURRENT_FY_ESTIMATE_FORMULA_RUNTIME_VERSION,
  canonicalizerVersion: CURRENT_FY_ESTIMATE_FORMULA_CANONICALIZER_VERSION,
  inputSchemaVersion: CURRENT_FY_ESTIMATE_FORMULA_INPUT_SCHEMA_VERSION,
  outputSchemaVersion: CURRENT_FY_ESTIMATE_FORMULA_OUTPUT_SCHEMA_VERSION,
});

function currentFyEstimateGoldenScope(): CurrentFyEstimateScope {
  return Object.freeze({ scopeKind: 'company', scopeId: 'example-enterprise' });
}

function currentFyEstimateGoldenFact(
  componentKind: CurrentFyEstimateFactComponentKind,
  valueUsd: number,
  periodStartMonth: number,
  periodEndMonth: number,
): CurrentFyEstimateFact {
  return Object.freeze({
    schemaVersion: CURRENT_FY_ESTIMATE_FACT_SCHEMA_VERSION,
    factId: `current-fy-estimate:${componentKind}`,
    componentKind,
    metricKey: 'operating_income_usd',
    metricValueKind: 'additive_usd' as const,
    fiscalYear: 2027,
    scope: currentFyEstimateGoldenScope(),
    currency: 'USD' as const,
    periodStartMonth,
    periodEndMonth,
    valueUsd,
    sourceRef: `finance-pnl:fy2027:${componentKind}`,
    reportingCutAsOf: '2027-08-31',
  });
}

const CURRENT_FY_ESTIMATE_OK_INPUT: CurrentFyEstimateInput = Object.freeze({
  facts: Object.freeze([
    currentFyEstimateGoldenFact('actual_to_date', 80000000, 1, 8),
    currentFyEstimateGoldenFact('forecast_remainder', 45000000, 9, 12),
  ]),
});

const CURRENT_FY_ESTIMATE_GAP_INPUT: CurrentFyEstimateInput = Object.freeze({
  facts: Object.freeze([
    currentFyEstimateGoldenFact('actual_to_date', 80000000, 1, 8),
    currentFyEstimateGoldenFact('forecast_remainder', 45000000, 10, 12),
  ]),
});

const CURRENT_FY_ESTIMATE_MISMATCH_INPUT: CurrentFyEstimateInput = Object.freeze({
  facts: Object.freeze([
    currentFyEstimateGoldenFact('actual_to_date', 80000000, 1, 8),
    Object.freeze({
      ...currentFyEstimateGoldenFact('forecast_remainder', 45000000, 9, 12),
      fiscalYear: 2028,
    }),
  ]),
});

const CURRENT_FY_ESTIMATE_GOLDEN_TRACES: readonly ApprovedFormulaGoldenTrace<
  CurrentFyEstimateInput,
  CurrentFyEstimateOutput
>[] = Object.freeze([
  Object.freeze({
    traceId: 'current_fy_estimate:actual-plus-remainder:v1',
    input: CURRENT_FY_ESTIMATE_OK_INPUT,
    expected: Object.freeze({
      status: 'ok' as const,
      output: Object.freeze({
        primaryMetricKey: 'operating_income_usd',
        primaryMetricValue: 125000000,
        metricValueKind: 'additive_usd' as const,
        fiscalYear: 2027,
        scope: currentFyEstimateGoldenScope(),
        currency: 'USD' as const,
        actualToDateUsd: 80000000,
        forecastRemainderUsd: 45000000,
        currentFyEstimateUsd: 125000000,
        actualPeriodStartMonth: 1,
        actualPeriodEndMonth: 8,
        forecastPeriodStartMonth: 9,
        forecastPeriodEndMonth: 12,
      }),
    }),
  }),
  Object.freeze({
    traceId: 'current_fy_estimate:gap-refusal:v1',
    input: CURRENT_FY_ESTIMATE_GAP_INPUT,
    expected: Object.freeze({
      status: 'blocked' as const,
      reasonCode: 'invalid_formula_input' as const,
      message:
        'actual_to_date and forecast_remainder must cover fiscal months 1 through 12 without gaps',
    }),
  }),
  Object.freeze({
    traceId: 'current_fy_estimate:fiscal-year-mismatch:v1',
    input: CURRENT_FY_ESTIMATE_MISMATCH_INPUT,
    expected: Object.freeze({
      status: 'blocked' as const,
      reasonCode: 'invalid_formula_input' as const,
      message: 'actual_to_date and forecast_remainder must use the same fiscalYear',
    }),
  }),
]);

type DigestSource = Readonly<{ toString(): string }>;

function implementationDigestInput(
  name: string,
  source: DigestSource,
): ApprovedFormulaImplementationDigestInput {
  return Object.freeze({ name, source: source.toString() });
}

export function getCurrentFyEstimateImplementationDigestInputs(): readonly ApprovedFormulaImplementationDigestInput[] {
  return Object.freeze([
    implementationDigestInput('blocked', blocked),
    implementationDigestInput('currentFyEstimateFactIssue', currentFyEstimateFactIssue),
    implementationDigestInput('currentFyEstimateScopeIssue', currentFyEstimateScopeIssue),
    implementationDigestInput('currentFyEstimateInputIssue', currentFyEstimateInputIssue),
    implementationDigestInput('currentFyEstimateIdentityIssue', currentFyEstimateIdentityIssue),
    implementationDigestInput('currentFyEstimatePeriodIssue', currentFyEstimatePeriodIssue),
    implementationDigestInput('sameCurrentFyEstimateScope', sameCurrentFyEstimateScope),
    implementationDigestInput('currentFyEstimatePair', currentFyEstimatePair),
    implementationDigestInput('roundUsd', roundUsd),
    implementationDigestInput('executeCurrentFyEstimate', executeCurrentFyEstimate),
    implementationDigestInput('evidenceBlockFromFormulaBlock', evidenceBlockFromFormulaBlock),
    implementationDigestInput('evidenceReconciliationBlock', evidenceReconciliationBlock),
    implementationDigestInput('sourceSystemRefApproved', sourceSystemRefApproved),
    implementationDigestInput('evidenceForFact', evidenceForFact),
    implementationDigestInput('sameOptionalString', sameOptionalString),
    implementationDigestInput('sameCurrentFyEstimateFact', sameCurrentFyEstimateFact),
    implementationDigestInput(
      'currentFyEstimateArtifactContentBlock',
      currentFyEstimateArtifactContentBlock,
    ),
    implementationDigestInput(
      'validateCurrentFyEstimateInputAgainstEvidence',
      validateCurrentFyEstimateInputAgainstEvidence,
    ),
    implementationDigestInput('reconcileCurrentFyEstimateOutput', reconcileCurrentFyEstimateOutput),
    implementationDigestInput('sameCurrentFyEstimateOutput', sameCurrentFyEstimateOutput),
    implementationDigestInput('isRecord', isRecord),
    implementationDigestInput('isNonEmptyString', isNonEmptyString),
    implementationDigestInput('isFiniteNumber', isFiniteNumber),
    implementationDigestInput('isFiscalMonth', isFiscalMonth),
    implementationDigestInput('validateCurrentFyEstimateFact', validateCurrentFyEstimateFact),
    valueDigestInput('CURRENT_FY_ESTIMATE_APPROVED_SOURCE_SYSTEM_REFS', [
      ...CURRENT_FY_ESTIMATE_APPROVED_SOURCE_SYSTEM_REFS,
    ]),
    valueDigestInput('CURRENT_FY_ESTIMATE_PERIOD_RULE', {
      completeFyStartMonth: COMPLETE_FY_START_MONTH,
      completeFyEndMonth: COMPLETE_FY_END_MONTH,
    }),
  ]);
}

const CURRENT_FY_ESTIMATE_RECONCILIATION_RULE: ApprovedFormulaReconciliationRule<
  CurrentFyEstimateInput,
  CurrentFyEstimateOutput
> = Object.freeze({
  kind: 'input_output_evidence_reconciliation',
  ruleId: 'current_fy_estimate_actual_forecast_reconciliation_v1',
  description:
    'Output must exactly match actual_to_date plus the complete non-overlapping forecast_remainder.',
  validate(input, output, artifacts) {
    void artifacts;
    return reconcileCurrentFyEstimateOutput(input, output);
  },
});

export const currentFyEstimateFormulaDefinition: ApprovedFormulaDefinition<
  CurrentFyEstimateInput,
  CurrentFyEstimateOutput
> = Object.freeze({
  ...CURRENT_FY_ESTIMATE_FORMULA_METADATA,
  expectedContextKind: CURRENT_FY_ESTIMATE_CONTEXT_KIND,
  approvedSourceSystemRefs: CURRENT_FY_ESTIMATE_APPROVED_SOURCE_SYSTEM_REFS,
  implementationDigestInputs: getCurrentFyEstimateImplementationDigestInputs(),
  goldenTraces: CURRENT_FY_ESTIMATE_GOLDEN_TRACES,
  validateInputAgainstEvidence: validateCurrentFyEstimateInputAgainstEvidence,
  reconciliationRule: CURRENT_FY_ESTIMATE_RECONCILIATION_RULE,
  execute: executeCurrentFyEstimate,
});

export const CURRENT_FY_ESTIMATE_APPROVED_FORMULA_DEFINITIONS = Object.freeze([
  currentFyEstimateFormulaDefinition,
] as const);
